/* eslint-disable jsx-a11y/no-static-element-interactions */
import Button from '@/components/Button';
import Container from '@/components/Container';
import Divider from '@/components/Divider';
import PageHeader from '@/components/PageHeader';
import {
  ComponentMindfulMinuteCalmBreathing,
  ComponentMindfulMinuteQuote,
  ComponentMindfulMinuteVideo,
  DailyBubblePage,
  Enum_Completedtask_Type,
  Enum_Componentcommonembedded_Provider,
  Enum_Mindfulminute_Type,
  FetchUserStreakQuery,
  FetchUserStreakQueryVariables,
  MindfulMinuteEntity,
  MindfulMinutePage,
  ResourceEntity,
} from '@/graphql/generated/graphql';
import { fetchLatestResourcesByTopic } from '@/services/knowledge-hub/knowledge-hub.service';
import React, { useEffect, useRef, useState } from 'react';
import { CalmBreathingAnimation } from './BreathingWheel';
import classNames from 'classnames';
import { completeTask } from '@/services/daily/complete-tasks.service';
import { getPage, onChangeOnboardStatus } from '@/services/daily/mindful-minute.service';
import DefaultContentTemplateEmbedded from '@/components/DefaultContentTemplate/Embedded';

import { useHomeStore } from '../home/useHome';
import { PauseIcon, PlayIcon, StopIcon } from '@heroicons/react/20/solid';
import { useAuthentication } from '@/hooks/useAuthentication';
import GenericOnboarding from '@/components/generic-onboarding';
import StatsAndStreak, { StatsAndStreakContainer } from '@/components/StatsAndStreak';
import { QUERY_FETCH_USER_STREAK } from '@/graphql/queries/daily/completed-tasks';
import { useQuery } from '@apollo/client';
import {
  clearMindfulMinuteDataLayer,
  getMindfulMinuteTitle,
  trackMindfulMinute,
} from '@/lib/ga-custom-events/mindfulMinute';
import { renderMeditationCards } from '../meditation/detail';

type RenderQuoteProps = {
  mindfulMinute: MindfulMinuteEntity;
  prompt?: ComponentMindfulMinuteQuote | null;
  setIsAnimating: React.Dispatch<React.SetStateAction<boolean>>;
  isAnimating: boolean;
  page: MindfulMinutePage | null;
  duration: number | null;
  setIsFinished: React.Dispatch<React.SetStateAction<boolean>>;
  audioRef: React.MutableRefObject<HTMLAudioElement | null>;
  isStarted: boolean;
  setIsStarted: (value: boolean) => void;
  key: number;
  setKey: React.Dispatch<React.SetStateAction<number>>;
  isFinished: boolean;
  mindfulMinuteCompleted?: boolean;
  mindfulMinuteData?: FetchUserStreakQuery;
  homePage?: DailyBubblePage | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
};

type RenderBreathingProps = {
  mindfulMinute: MindfulMinuteEntity;
  calmBreathing?: ComponentMindfulMinuteCalmBreathing | null;
  setIsAnimating: React.Dispatch<React.SetStateAction<boolean>>;
  isAnimating: boolean;
  page: MindfulMinutePage | null;
  duration: number | null;
  setIsFinished: React.Dispatch<React.SetStateAction<boolean>>;
  audioRef: React.MutableRefObject<HTMLAudioElement | null>;
  isStarted: boolean;
  setIsStarted: (value: boolean) => void;
  key: number;
  setKey: React.Dispatch<React.SetStateAction<number>>;
  isFinished: boolean;
  mindfulMinuteCompleted?: boolean;
  mindfulMinuteData?: FetchUserStreakQuery;
  homePage?: DailyBubblePage | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
};

type RenderVideoProps = {
  mindfulMinute: MindfulMinuteEntity;
  video?: ComponentMindfulMinuteVideo | null;
  setIsAnimating: React.Dispatch<React.SetStateAction<boolean>>;
  setDuration: React.Dispatch<React.SetStateAction<number | null>>;
  page: MindfulMinutePage | null;
  setIsFinished: React.Dispatch<React.SetStateAction<boolean>>;
  isStarted: boolean;
  setIsStarted: (value: boolean) => void;
  isFinished: boolean;
  mindfulMinuteCompleted?: boolean;
  mindfulMinuteData?: FetchUserStreakQuery;
  homePage?: DailyBubblePage | null;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
};

const renderQuote = ({
  mindfulMinute,
  prompt,
  setIsAnimating,
  isAnimating,
  page,
  duration,
  setIsFinished,
  audioRef,
  isStarted,
  setIsStarted,
  isFinished,
  key,
  setKey,
  mindfulMinuteData,
  mindfulMinuteCompleted,
  homePage,
}: RenderQuoteProps) => {
  return (
    <>
      <div className=" grid grid-cols-1 xl:grid-cols-2">
        <div className="flex items-center justify-center px-5">
          <div className="flex flex-col items-center text-center">
            <>
              {prompt?.title && !isFinished ? (
                <h2 className="mb-sh-15 font-larken text-sh-md">{prompt?.title}</h2>
              ) : null}
              {page?.QuotePage?.thankYouTitle && isFinished ? (
                <h2 className="mb-sh-15 font-larken text-sh-md">{page?.QuotePage?.thankYouTitle}</h2>
              ) : null}
              {!isFinished && (
                <div
                  className="text-center [&>p]:text-sh-14-18"
                  dangerouslySetInnerHTML={{
                    __html: prompt?.prompt || '',
                  }}
                />
              )}

              {mindfulMinuteCompleted || isFinished ? (
                <StatsAndStreakContainer className="mt-sh-30">
                  <StatsAndStreak
                    type="MINDFUL"
                    value={mindfulMinuteData?.fetchUserStreak?.total || 0}
                    unit={homePage?.finishingCard?.finishingCardMindfulMinute?.minutes || 'Mindful minutes'}
                    title={homePage?.finishingCard?.finishingCardMindfulMinute?.youHaveWatched || "You've watched"}
                    color="MINDFUL"
                  />
                  <StatsAndStreak
                    type="STREAK"
                    value={mindfulMinuteData?.fetchUserStreak?.streak || 0}
                    unit={homePage?.finishingCard?.finishingCardMindfulMinute?.days || 'Days'}
                    title={homePage?.finishingCard?.finishingCardMindfulMinute?.yourStreak || 'Your streak'}
                    color="MINDFUL"
                  />
                </StatsAndStreakContainer>
              ) : null}

              <div className="mt-sh-30 flex gap-sh-15">
                {isStarted && !isFinished && (
                  <>
                    <Button
                      onClick={() => {
                        if (isAnimating) {
                          audioRef?.current?.pause();
                        } else {
                          audioRef?.current?.play();
                        }
                        setIsAnimating(!isAnimating);
                      }}
                      theme={'solid'}
                      className={classNames('flex items-center justify-center transition duration-300 ease-in', {})}>
                      {isAnimating ? (
                        <PauseIcon className="inline-block h-4 w-4" />
                      ) : (
                        <PlayIcon className="inline-block h-4 w-4" />
                      )}
                      {isAnimating
                        ? `${page?.QuotePage?.pauseButtonLabel || 'Pause'}`
                        : `${page?.QuotePage?.playButtonLabel || 'Play'}`}
                    </Button>
                    <Button
                      onClick={() => {
                        setIsAnimating(false);
                        setKey((prevKey: number) => prevKey + 1);
                      }}
                      theme={'solid'}
                      className={classNames('flex items-center justify-center transition duration-300 ease-in', {})}>
                      <StopIcon className="inline-block h-4 w-4" />
                      {page?.QuotePage?.stopButtonLabel || 'Stop'}
                    </Button>
                  </>
                )}

                {!isStarted && !isFinished && (
                  <Button
                    onClick={() => {
                      audioRef?.current?.play();
                      setIsStarted(true);
                      setIsAnimating(true);
                    }}
                    disabled={isAnimating}
                    theme={'solid'}
                    className={classNames('flex items-center justify-center transition duration-300 ease-in', {})}>
                    {page?.QuotePage?.startExerciseButton || 'Start exercise'}
                  </Button>
                )}

                {isFinished && (
                  <Button
                    onClick={() => {
                      setIsStarted(true);
                      setKey((prevKey: number) => prevKey + 1);
                      setIsFinished(false);
                    }}
                    disabled={isAnimating}
                    theme={'solid'}
                    className={classNames('flex items-center justify-center transition duration-300 ease-in', {})}>
                    {page?.QuotePage?.restartButtonLabel || 'Try the exercise again'}
                  </Button>
                )}
              </div>
            </>
          </div>
        </div>
        <div className="mt-10 flex items-center justify-center lg:mt-0">
          {duration ? (
            <CalmBreathingAnimation
              key={key}
              totalTime={duration}
              isAnimating={isAnimating}
              onFinish={async () => {
                await completeTask({
                  entity: mindfulMinute,
                  type: Enum_Completedtask_Type.MindfulMinute,
                });
                setIsStarted(false);
                setIsFinished(true);
                setIsAnimating(false);
              }}
            />
          ) : null}
        </div>
      </div>
    </>
  );
};

const renderBreathing = ({
  mindfulMinute,
  calmBreathing,
  setIsAnimating,
  isAnimating,
  page,
  duration,
  setIsFinished,
  isFinished,
  audioRef,
  isStarted,
  setIsStarted,
  key,
  setKey,
  mindfulMinuteData,
  mindfulMinuteCompleted,
  homePage,
}: RenderBreathingProps) => {
  return (
    <>
      <div className="mb-20 grid grid-cols-1 xl:grid-cols-2">
        <div className="flex items-center justify-center px-5">
          <div className="flex flex-col items-center text-center">
            <>
              {calmBreathing?.title && !isFinished && (
                <h2 className="mb-sh-15 font-larken text-sh-md">{calmBreathing?.title}</h2>
              )}
              {page?.CalmBreathingPage?.thankYouTitle && isFinished ? (
                <h2 className="mb-sh-15 font-larken text-sh-md">{page?.CalmBreathingPage?.thankYouTitle}</h2>
              ) : null}
              <div
                className="text-center [&>p]:text-sh-14-18"
                dangerouslySetInnerHTML={{
                  __html:
                    (mindfulMinute?.attributes?.type === Enum_Mindfulminute_Type.Breathing && calmBreathing?.prompt) ||
                    '',
                }}
              />

              {mindfulMinuteCompleted || isFinished ? (
                <StatsAndStreakContainer className="mt-sh-30">
                  <StatsAndStreak
                    type="MINDFUL"
                    value={mindfulMinuteData?.fetchUserStreak?.total || 0}
                    unit={homePage?.finishingCard?.finishingCardMindfulMinute?.minutes || 'Mindful minutes'}
                    title={homePage?.finishingCard?.finishingCardMindfulMinute?.youHaveWatched || "You've watched"}
                    color="MINDFUL"
                  />
                  <StatsAndStreak
                    type="STREAK"
                    value={mindfulMinuteData?.fetchUserStreak?.streak || 0}
                    unit={homePage?.finishingCard?.finishingCardMindfulMinute?.minutes || 'Mindful minutes'}
                    title={homePage?.finishingCard?.finishingCardMindfulMinute?.youHaveWatched || "You've watched"}
                    color="MINDFUL"
                  />
                </StatsAndStreakContainer>
              ) : null}

              <div className="mt-sh-30 flex gap-sh-15">
                {isStarted && !isFinished && (
                  <>
                    <Button
                      onClick={() => {
                        if (isAnimating) {
                          audioRef?.current?.pause();
                        } else {
                          audioRef?.current?.play();
                        }
                        setIsAnimating(!isAnimating);
                      }}
                      theme={'solid'}
                      className={classNames('flex items-center justify-center transition duration-300 ease-in', {})}>
                      {isAnimating ? (
                        <PauseIcon className="inline-block h-4 w-4" />
                      ) : (
                        <PlayIcon className="inline-block h-4 w-4" />
                      )}
                      {isAnimating
                        ? `${page?.CalmBreathingPage?.pauseButtonLabel || 'Pause'}`
                        : `${page?.CalmBreathingPage?.playButtonLabel || 'Play'}`}
                    </Button>
                    <Button
                      onClick={() => {
                        setIsAnimating(false);
                        setKey((prevKey: number) => prevKey + 1);
                      }}
                      theme={'solid'}
                      className={classNames('flex items-center justify-center transition duration-300 ease-in', {})}>
                      <StopIcon className="inline-block h-4 w-4" />
                      {page?.CalmBreathingPage?.stopButtonLabel || 'Stop'}
                    </Button>
                  </>
                )}

                {!isStarted && !isFinished && (
                  <Button
                    onClick={() => {
                      setIsStarted(true);
                      audioRef?.current?.play();
                      setIsAnimating(true);
                    }}
                    disabled={isAnimating}
                    theme={'solid'}
                    className={classNames('flex items-center justify-center transition duration-300 ease-in', {})}>
                    {page?.CalmBreathingPage?.startExerciseButton || 'Start exercise'}
                  </Button>
                )}

                {isFinished && (
                  <Button
                    onClick={() => {
                      setIsStarted(true);
                      setKey((prevKey: number) => prevKey + 1);
                      setIsFinished(false);
                    }}
                    disabled={isAnimating}
                    theme={'solid'}
                    className={classNames('flex items-center justify-center transition duration-300 ease-in', {})}>
                    {page?.QuotePage?.restartButtonLabel || 'Try the exercise again'}
                  </Button>
                )}
              </div>
            </>
          </div>
        </div>
        <div className="mt-10 flex items-center justify-center lg:mt-0">
          {duration ? (
            <CalmBreathingAnimation
              key={key}
              totalTime={duration}
              isAnimating={isAnimating}
              onFinish={async () => {
                await completeTask({
                  entity: mindfulMinute,
                  type: Enum_Completedtask_Type.MindfulMinute,
                });

                setIsFinished(true);
                setIsAnimating(false);
              }}
            />
          ) : null}
        </div>
      </div>
    </>
  );
};

const renderVideo = ({
  mindfulMinute,
  video,
  setIsStarted,
  isStarted,
  setIsAnimating,
  setIsFinished,
  setDuration,
  isFinished,
  mindfulMinuteCompleted,
  mindfulMinuteData,
  homePage,
}: RenderVideoProps) => {
  return (
    <>
      <div className="grid grid-cols-1">
        <div className="flex items-center justify-center">
          <div className="relative flex w-full flex-col items-center text-center">
            {video?.videoTitle ? <h2 className="mb-sh-15 font-larken text-sh-md">{video?.videoTitle}</h2> : null}
            {video?.video?.data?.attributes && (
              <DefaultContentTemplateEmbedded
                className="mindful-video !my-0"
                onVideoPlay={() => {
                  if (!isStarted) {
                    setIsStarted(true);
                  }
                }}
                component={{
                  id: 'DefaultContentTemplateEmbedded',
                  link: video?.video?.data?.attributes?.url,
                  provider: Enum_Componentcommonembedded_Provider.Youtube,
                }}
                onDuration={(n) => {
                  setDuration(n);
                }}
                onEnded={async () => {
                  await completeTask({
                    entity: mindfulMinute,
                    type: Enum_Completedtask_Type.MindfulMinute,
                  });
                  setIsFinished(true);
                  setIsAnimating(false);
                }}
              />
            )}
          </div>
        </div>
        {mindfulMinuteCompleted || isFinished ? (
          <StatsAndStreakContainer className="mt-sh-30">
            <StatsAndStreak
              type="MINDFUL"
              value={mindfulMinuteData?.fetchUserStreak?.total || 0}
              unit={homePage?.finishingCard?.finishingCardMindfulMinute?.minutes || 'Mindful minutes'}
              title={homePage?.finishingCard?.finishingCardMindfulMinute?.youHaveWatched || "You've watched"}
              color="MINDFUL"
            />
            <StatsAndStreak
              type="STREAK"
              value={mindfulMinuteData?.fetchUserStreak?.streak || 0}
              unit={homePage?.finishingCard?.finishingCardMindfulMinute?.minutes || 'Mindful minutes'}
              title={homePage?.finishingCard?.finishingCardMindfulMinute?.youHaveWatched || "You've watched"}
              color="MINDFUL"
            />
          </StatsAndStreakContainer>
        ) : null}
      </div>
    </>
  );
};

export default function MindfulMinuteUIPage() {
  const [youMayAlso, setYouMayAlso] = useState<ResourceEntity[]>([]);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [page, setPage] = useState<MindfulMinutePage | null>(null);
  const { mindfulMinute, handleMindfulMinute } = useHomeStore();
  const [isStarted, setIsStartedState] = useState(false);
  const [key, setKey] = React.useState(0);
  const { authentication, setAuthentication } = useAuthentication();
  const { mindfulMinuteCompleted, page: homePage, fetchHomePage } = useHomeStore();

  const { calmBreathing, prompt, video } = mindfulMinute?.attributes || {};
  const type = mindfulMinute?.attributes?.type;

  const [duration, setDuration] = useState<number | null>(null);

  const setIsStarted = (state: boolean) => {
    setIsStartedState(state);
  };

  const audioFile = calmBreathing?.spokenGuidance?.data?.attributes?.url || '';
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const { data: mindfulMinuteData, refetch } = useQuery<FetchUserStreakQuery, FetchUserStreakQueryVariables>(
    QUERY_FETCH_USER_STREAK,
    {
      variables: { type: 'MINDFUL_MINUTE' },
      fetchPolicy: 'network-only',
    },
  );

  useEffect(() => {
    if (isFinished) {
      refetch();
    }
  }, [isFinished, refetch]);

  useEffect(() => {
    async function getYouMayAlso() {
      const page = await getPage();

      setPage(page?.page);

      if (!page?.page?.relatedContent?.data?.attributes?.topic) {
        return;
      }

      const latests = await fetchLatestResourcesByTopic(page?.page?.relatedContent?.data?.attributes?.topic, 2);

      if (latests.resorces === null) {
        return;
      }

      if (latests.resorces!.length! > 0) {
        setYouMayAlso(latests.resorces as ResourceEntity[]);
      }
    }

    getYouMayAlso();

    return () => {
      clearMindfulMinuteDataLayer();
    };
  }, []);

  useEffect(() => {
    if (isFinished) {
      trackMindfulMinute({
        date: mindfulMinute?.attributes?.date,
        title: getMindfulMinuteTitle(mindfulMinute!) || '',
        mindfulMinutetype: type || '',
      });
    }

    if (type !== Enum_Mindfulminute_Type.Video && (!audioFile || !audioRef.current)) {
      return;
    }

    if (isAnimating) {
      audioRef?.current?.play();
    }

    if (isFinished) {
      audioRef?.current?.pause();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAnimating, audioFile, audioRef, isFinished]);

  useEffect(() => {
    fetchHomePage();
    handleMindfulMinute();
  }, [fetchHomePage, handleMindfulMinute]);

  useEffect(() => {
    if (audioRef?.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setIsStarted(false);
  }, [key, audioRef]);

  useEffect(() => {
    if (type === Enum_Mindfulminute_Type.Prompt && !audioFile) {
      setDuration(prompt?.durationInSeconds);
    }

    if (type === Enum_Mindfulminute_Type.Breathing && audioFile) {
      audioRef.current = new Audio(audioFile);
      audioRef.current.onloadedmetadata = () => {
        setDuration(audioRef.current?.duration ?? null);
      };
    }
  }, [type, audioFile, prompt?.durationInSeconds]);

  const renderContent = () => {
    if (!authentication.user.onboardMindfulMinuteCompleted) {
      return (
        <GenericOnboarding
          showStepper={false}
          onboarding={page?.onboarding}
          onFinish={async () => {
            await onChangeOnboardStatus();
            setAuthentication({
              ...authentication,
              user: {
                ...authentication.user,
                onboardMindfulMinuteCompleted: !authentication.user.onboardMindfulMinuteCompleted,
              },
            });
          }}
        />
      );
    }

    return mindfulMinute?.id ? (
      <div className="col-span-12 mt-sh-15 gap-4 lg:col-start-3 lg:col-end-11">
        <div>
          {type === Enum_Mindfulminute_Type.Prompt
            ? renderQuote({
                mindfulMinute,
                prompt,
                setIsAnimating,
                isAnimating,
                page,
                duration,
                setIsFinished,
                audioRef,
                isStarted,
                setIsStarted,
                key,
                setKey,
                isFinished,
                mindfulMinuteCompleted,
                mindfulMinuteData,
                homePage,
              })
            : null}
          {type === Enum_Mindfulminute_Type.Breathing
            ? renderBreathing({
                mindfulMinute,
                calmBreathing,
                setIsAnimating,
                isAnimating,
                page,
                duration,
                setIsFinished,
                audioRef,
                isStarted,
                setIsStarted,
                key,
                setKey,
                isFinished,
                mindfulMinuteCompleted,
                mindfulMinuteData,
                homePage,
              })
            : null}
          {type === Enum_Mindfulminute_Type.Video
            ? renderVideo({
                mindfulMinute,
                video,
                setIsAnimating,
                setDuration,
                isStarted,
                setIsStarted,
                page,
                setIsFinished,
                isFinished,
                mindfulMinuteCompleted,
                mindfulMinuteData,
                homePage,
              })
            : null}
        </div>
      </div>
    ) : null;
  };

  return (
    <div className="min-h-[46rem]">
      <Container className="mt-sh-60">
        <div className="col-span-12 gap-4 lg:col-start-3 lg:col-end-11">
          <PageHeader title="Mindful Minute" url="/" type="mindful" />
        </div>
        {renderContent()}

        <div className="col-span-12 mt-sh-15 gap-4 lg:col-start-3 lg:col-end-11">
          <Divider />

          <div className={youMayAlso.length > 0 ? 'mt-sh-30' : ''}>
            {renderMeditationCards({
              title: page?.relatedContentTitle || 'You may also like',
              data: page?.mindfulExerciseRelatedContent?.data || [],
              showDivider: false,
            })}
          </div>
        </div>
      </Container>
    </div>
  );
}
